// Deleted Records — the READ-ONLY ledger of everything removed from the panel.
//
// Every delete anywhere (orders, payments, dispatch, returns, stock, money…)
// lands here with WHO removed it, WHEN, WHY, and a full snapshot of the data
// as it stood. There is no edit and no undo on this screen by design — an
// audit trail you can rewrite is not an audit trail.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiChevronDown,
  FiChevronUp,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiTrash2,
  FiUser,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert, Button } from '../components/common/index.js';
import { deletionsApi } from '../api/deletionsApi';

const fmtMoney = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : null;
};

const SECTION_TONES = {
  orders: 'border-sky-200 bg-sky-50 text-sky-700',
  payments: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  dispatch: 'border-violet-200 bg-violet-50 text-violet-700',
  returns: 'border-rose-200 bg-rose-50 text-rose-700',
  stock: 'border-amber-200 bg-amber-50 text-amber-700',
  stock_sale: 'border-amber-200 bg-amber-50 text-amber-700',
  outside_parts: 'border-teal-200 bg-teal-50 text-teal-700',
  outside_part_sale: 'border-teal-200 bg-teal-50 text-teal-700',
  money: 'border-indigo-200 bg-indigo-50 text-indigo-700',
  trade_in_sale: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700',
  other: 'border-slate-200 bg-slate-50 text-slate-600',
};

// ── Snapshot rendering ────────────────────────────────────────────────────────
// A deleted order's snapshot carries the parts list, the payment block, the
// returns, the follow-ups — and the superadmin opens this screen precisely to
// READ them ("what did we just lose?"). A JSON dump answers that question the
// way a database dump would: technically, and to nobody. Everything below
// turns the known shapes into tables and label/value rows; raw JSON survives
// only as the last-resort fallback for a shape nothing recognises.

const looksMoney = (k) =>
  /(price|amount|total|balance|due|refund|value|advance|cash_back)$/i.test(k);

const humanize = (k) => String(k).replaceAll('_', ' ');

const fmtCell = (k, v) => {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? '✓' : '—';
  if (looksMoney(k)) {
    const m = fmtMoney(v);
    if (m) return m;
  }
  return String(v);
};

/** What happened to this line — the one thing a per-part row must say. */
const partStatus = (p) => {
  if (p.is_cancelled) return { label: 'Cancelled', cls: 'bg-red-50 text-red-600' };
  if (p.return_state === 'exchanged') return { label: 'Exchanged', cls: 'bg-fuchsia-50 text-fuchsia-600' };
  if (p.return_state === 'returned' || p.returned) return { label: 'Returned', cls: 'bg-rose-50 text-rose-600' };
  if (p.return_state === 'cancelled') return { label: 'Cancelled', cls: 'bg-red-50 text-red-600' };
  if (p.osm_unavailable) return { label: 'OSM refunded', cls: 'bg-amber-50 text-amber-700' };
  if (p.is_available === false) return { label: 'Not available', cls: 'bg-slate-100 text-slate-500' };
  if (p.dispatched) return { label: 'Dispatched', cls: 'bg-emerald-50 text-emerald-600' };
  return null;
};

/** The parts of a deleted order, as an order reads: name, qty, price, where
 *  it came from, and what became of it. */
const PartsTable = ({ parts }) => (
  <div className="overflow-x-auto rounded-xl border border-slate-200">
    <table className="w-full text-left text-xs">
      <thead>
        <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-bold uppercase tracking-wide text-slate-400">
          <th className="px-3 py-2">Part</th>
          <th className="px-3 py-2 text-right">Price</th>
          <th className="px-3 py-2">Source</th>
          <th className="px-3 py-2">Status</th>
        </tr>
      </thead>
      <tbody>
        {parts.map((p, i) => {
          const st = partStatus(p);
          const voided = st && ['Cancelled', 'Returned', 'Exchanged'].includes(st.label);
          return (
            <tr key={p.id ?? i} className="border-b border-slate-100 last:border-b-0">
              <td className={`px-3 py-2 font-semibold ${voided ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                {p.name}
                {Number(p.quantity) > 1 && (
                  <span className="ml-1 font-normal text-slate-400">×{p.quantity}</span>
                )}
                {p.cancel_reason && (
                  <span className="ml-1 font-normal italic text-slate-400">— {p.cancel_reason}</span>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right font-semibold text-slate-800">
                {/* Trade-in: the price that counted, with the normal one struck. */}
                {p.exchange_selected && p.normal_price ? (
                  <>
                    <span className="mr-1 text-slate-400 line-through">{fmtMoney(p.normal_price)}</span>
                    {fmtMoney(p.exchange_price) ?? '—'}
                  </>
                ) : (
                  fmtMoney(p.price) ?? '—'
                )}
              </td>
              <td className="px-3 py-2 uppercase text-slate-500">{p.source || '—'}</td>
              <td className="px-3 py-2">
                {st
                  ? <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${st.cls}`}>{st.label}</span>
                  : <span className="text-slate-300">—</span>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

/** Any array of uniform objects (returns, follow-ups, OSM advances…) as a
 *  table. Columns that never carry a value are dropped, not shown empty. */
const GenericTable = ({ rows }) => {
  const cols = [];
  const seen = new Set();
  rows.forEach((r) => Object.keys(r || {}).forEach((k) => {
    if (!seen.has(k)) { seen.add(k); cols.push(k); }
  }));
  const keep = cols.filter((k) => rows.some((r) => {
    const v = r?.[k];
    return v != null && v !== '' && v !== false && typeof v !== 'object';
  }));
  if (keep.length === 0) return <p className="text-xs text-slate-400">—</p>;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-bold uppercase tracking-wide text-slate-400">
            {keep.map((k) => <th key={k} className="whitespace-nowrap px-3 py-2">{humanize(k)}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-b-0">
              {keep.map((k) => (
                <td key={k} className="px-3 py-2 text-slate-700">{fmtCell(k, r?.[k])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const KeyValueGrid = ({ obj, depth = 0 }) => {
  const entries = Object.entries(obj || {});
  const simple = entries.filter(([, v]) => v == null || typeof v !== 'object');
  const nested = entries.filter(([, v]) => v != null && typeof v === 'object'
    && (Array.isArray(v) ? v.length > 0 : Object.keys(v).length > 0));
  return (
    <div className="space-y-3">
      {simple.length > 0 && (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {simple.map(([k, v]) => (
            <div key={k} className="flex items-baseline justify-between gap-2 border-b border-slate-100 pb-1">
              <dt className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {humanize(k)}
              </dt>
              <dd className="truncate text-right text-xs font-semibold text-slate-800">
                {fmtCell(k, v)}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {nested.map(([k, v]) => (
        <div key={k}>
          <p className="mb-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
            {humanize(k)}
          </p>
          <ComplexValue name={k} value={v} depth={depth + 1} />
        </div>
      ))}
    </div>
  );
};

/** Route one nested value to the right renderer. Depth-capped: below two
 *  levels a snapshot is no longer being read, and JSON is the honest form. */
const ComplexValue = ({ name, value, depth = 0 }) => {
  if (depth > 2) {
    return (
      <pre className="max-h-64 overflow-auto rounded-xl bg-slate-900 p-3 text-[11px] leading-5 text-slate-200">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <p className="text-xs text-slate-400">—</p>;
    if (value.every((v) => v == null || typeof v !== 'object')) {
      return (
        <div className="flex flex-wrap gap-1.5">
          {value.map((v, i) => (
            <span key={i} className="max-w-[16rem] truncate rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
              {String(v)}
            </span>
          ))}
        </div>
      );
    }
    if (name === 'parts' && value.every((v) => v && typeof v === 'object' && 'name' in v)) {
      return <PartsTable parts={value} />;
    }
    return <GenericTable rows={value} />;
  }
  if (value && typeof value === 'object') {
    return <KeyValueGrid obj={value} depth={depth} />;
  }
  return <p className="text-xs font-semibold text-slate-800">{fmtCell(name, value)}</p>;
};

export const SnapshotView = ({ snapshot }) => {
  const entries = Object.entries(snapshot || {});
  if (entries.length === 0) {
    return <p className="text-xs text-slate-400">No snapshot stored.</p>;
  }
  const simple = entries.filter(([, v]) => v == null || ['string', 'number', 'boolean'].includes(typeof v));
  // Empty arrays/objects say nothing worth a heading — drop them outright.
  const complex = entries.filter(([, v]) => v != null && typeof v === 'object'
    && (Array.isArray(v) ? v.length > 0 : Object.keys(v).length > 0));
  return (
    <div className="space-y-4">
      {simple.length > 0 && (
        <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {simple.map(([k, v]) => (
            <div key={k} className="flex items-baseline justify-between gap-2 border-b border-slate-100 pb-1">
              <dt className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {humanize(k)}
              </dt>
              <dd className="truncate text-right text-xs font-semibold text-slate-800">
                {fmtCell(k, v)}
              </dd>
            </div>
          ))}
        </dl>
      )}
      {complex.map(([k, v]) => (
        <div key={k}>
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">
            {humanize(k)}
          </p>
          <ComplexValue name={k} value={v} />
        </div>
      ))}
    </div>
  );
};

const RecordRow = ({ record }) => {
  const [open, setOpen] = useState(false);
  const tone = SECTION_TONES[record.section] || SECTION_TONES.other;
  const amount = fmtMoney(record.amount);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${tone}`}>
          {record.section_label}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-bold text-slate-900">{record.object_label || '—'}</span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-400">
            <span className="inline-flex items-center gap-1"><FiUser className="h-3 w-3" />{record.deleted_by || '—'}</span>
            <span>{record.created_at}</span>
            {record.reason && <span className="italic text-slate-500">“{record.reason}”</span>}
          </span>
        </span>
        {amount && <span className="shrink-0 text-sm font-black text-slate-900">{amount}</span>}
        {open
          ? <FiChevronUp className="h-4 w-4 shrink-0 text-slate-400" />
          : <FiChevronDown className="h-4 w-4 shrink-0 text-slate-400" />}
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 py-4">
          <SnapshotView snapshot={record.snapshot} />
        </div>
      )}
    </div>
  );
};

const DeletedRecordsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [section, setSection] = useState('');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (section) params.section = section;
      if (q.trim()) params.q = q.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await deletionsApi.getList(params);
      setData(res.data ?? res);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load deleted records');
    } finally {
      setLoading(false);
    }
  }, [section, q, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const records = data?.records ?? [];
  const counts = useMemo(() => data?.counts ?? {}, [data]);
  const totalAll = useMemo(
    () => Object.values(counts).reduce((a, b) => a + b, 0),
    [counts],
  );
  // Only offer tabs for sections that actually have deletions — an empty
  // 11-tab row would just be noise.
  const tabs = useMemo(() => ([
    { value: '', label: 'All', count: totalAll },
    ...(data?.sections ?? [])
      .filter((s) => counts[s.value] > 0)
      .map((s) => ({ value: s.value, label: s.label, count: counts[s.value] })),
  ]), [data, counts, totalAll]);

  return (
    <MainLayout>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-black text-slate-900">
            <FiTrash2 className="h-5 w-5 text-rose-500" /> Deleted Records
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Everything removed from the panel — who, when, why, and a full copy of the data. Read-only.
          </p>
        </div>
        <Button variant="secondary" onClick={load} className="gap-2">
          <FiRefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="mb-4 flex items-start gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3">
        <FiShield className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
        <p className="text-xs leading-5 text-slate-500">
          Nothing here can be edited or removed. Every delete anywhere in the panel writes a permanent
          entry with the deleting admin's name and a snapshot of the record as it stood.
        </p>
      </div>

      {error && <div className="mb-4"><Alert type="error" message={error} /></div>}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {tabs.map((t) => (
          <button
            key={t.value || 'all'}
            type="button"
            onClick={() => setSection(t.value)}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-bold transition ${
              section === t.value
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'
            }`}
          >
            {t.label}
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${section === t.value ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customer, record, admin or reason"
            className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
          />
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="rounded-xl border border-slate-200 px-2.5 py-2 text-sm text-slate-600 focus:border-slate-400 focus:outline-none" />
          <span className="text-xs text-slate-400">to</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="rounded-xl border border-slate-200 px-2.5 py-2 text-sm text-slate-600 focus:border-slate-400 focus:outline-none" />
        </div>
      </div>

      {loading ? (
        <p className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-400">Loading…</p>
      ) : records.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-16 text-center">
          <FiTrash2 className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 text-sm font-semibold text-slate-700">
            {totalAll === 0 ? 'Nothing has been deleted' : 'No records match your filters'}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            {totalAll === 0
              ? 'When you delete anything anywhere in the panel, its record appears here.'
              : 'Try a different section, search or date range.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {records.map((r) => <RecordRow key={r.id} record={r} />)}
        </div>
      )}
    </MainLayout>
  );
};

export default DeletedRecordsPage;
